import React, { useState } from 'react';
import { Banner, Button, Modal, Spinner, Textarea } from '../../design-system';
import { FINDING_STATUS_LABEL } from '../../design-system/severity';
import { FINDING_STATUS_REASON_MAX_LENGTH, FindingStatus } from '../../types/finding';
import { FindingStatusReasonPrompt, PendingStatusReason } from '../../hooks/useFindingStatusUpdate';
import { getFindingTitle } from '../../utils/findingUtils';

const QUESTION: Partial<Record<FindingStatus, string>> = {
  resolved: 'How was this finding resolved?',
  ignored: 'Why is this finding being ignored?',
  false_positive: 'Why is this a false positive?',
};

const REASON_FIELD_ID = 'finding-status-reason';

/**
 * Asks for the reason behind a status change. Shown when the organization requires one
 * to close a finding without a fix. Driven by `useFindingStatusUpdate().reasonPrompt`.
 */
const FindingStatusReasonModal: React.FC<FindingStatusReasonPrompt> = ({ pending, ...prompt }) => {
  if (!pending) return null;

  // Keyed by the change being asked about, so each new prompt mounts with an empty field
  // while a failed submit keeps what was typed. Do not clear the field from an effect
  // instead: the prompt can be opened by a server response, and an effect then runs
  // after the first input and wipes it.
  return <ReasonPrompt key={`${pending.finding.id}:${pending.status}`} pending={pending} {...prompt} />;
};

type ReasonPromptProps = Omit<FindingStatusReasonPrompt, 'pending'> & { pending: PendingStatusReason };

const ReasonPrompt: React.FC<ReasonPromptProps> = ({ pending, isSubmitting, error, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');

  const label = FINDING_STATUS_LABEL[pending.status];
  const canSubmit = reason.trim().length > 0 && !isSubmitting;
  const submit = () => {
    if (canSubmit) onConfirm(reason);
  };

  return (
    <Modal
      isOpen
      // Closing mid-request would hide whether the change went through.
      onClose={isSubmitting ? () => undefined : onCancel}
      closeOnBackdrop={!isSubmitting}
      title={`Mark as ${label}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            icon={isSubmitting ? <Spinner size={16} tone="current" /> : undefined}
          >
            {isSubmitting ? 'Saving...' : `Mark as ${label}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[13.5px] font-semibold text-[color:var(--text-primary)] break-words">
          {getFindingTitle(pending.finding)}
        </p>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={REASON_FIELD_ID} className="text-[13px] font-medium text-[color:var(--text-secondary)]">
            {QUESTION[pending.status] ?? 'Why is the status changing?'}
          </label>
          <Textarea
            id={REASON_FIELD_ID}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) submit();
            }}
            rows={4}
            maxLength={FINDING_STATUS_REASON_MAX_LENGTH}
            disabled={isSubmitting}
            required
            autoFocus
          />
          <p className="text-[12px] text-[color:var(--text-muted)]">
            Your organization requires a reason to close a finding without a fix. It is saved in the finding's
            history.
          </p>
        </div>
        {error && <Banner variant="error">{error}</Banner>}
      </div>
    </Modal>
  );
};

export default FindingStatusReasonModal;
